import React, { useCallback, useEffect, useId, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import Dialog from '../../common/Dialog';
import { ConfirmDialog, ReasonDialog, useToast } from '../../common';
import { useAuth } from '../../../context/authcontext';
import { useCanPerform } from '../../../hooks/useCanPerform';
import { DischargeSummaryService } from '../../../services/patientmr/dischargeSummary.service';
import { describeApiError } from '../../../utils/apiErrors';
import type {
  AddDeliveryRequest,
  DischargeSummary,
  DischargeSummaryNarrative,
  GoalOutcome,
} from '../../../dtos/patientmr/dischargeSummary.dto';
import {
  DELIVERY_METHOD_OPTIONS,
  DUE_LABEL,
  DUE_STYLE,
  GOAL_OUTCOME_OPTIONS,
  KIND_LABEL,
  NARRATIVE_FIELDS,
  RECIPIENT_ROLE_OPTIONS,
  STATUS_LABEL,
  formatUsDate,
  labelOf,
} from './dischargeSummaryLabels';

export interface DischargeSummaryDialogProps {
  open: boolean;
  patientId: string;
  summaryId: string | null;
  onClose: () => void;
  /** Called after any change so lists and banners can refresh */
  onChanged?: (summary: DischargeSummary) => void;
}

type Mode = 'view' | 'amend';
type Pending = 'sign' | 'qa' | 'approve' | 'return' | 'amend' | 'close' | null;

const button = 'min-h-[40px] rounded-md border px-3 text-sm font-medium disabled:opacity-50';
const primary = `${button} border-teal-700 bg-teal-700 text-white hover:bg-teal-800`;
const secondary = `${button} border-slate-300 bg-white text-slate-800 hover:bg-slate-50`;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Discharge / transfer summary (Phase 16E, DSC-10..18). Facts come from the record (refresh after
 * fixing the source); the author writes the narrative and goal outcomes, then signs or sends it to
 * QA. A signed summary changes only by amendment. "Mark sent" records a delivery made outside
 * Pelora; the 5 / 2 business-day clock shows until then.
 */
const DischargeSummaryDialog: React.FC<DischargeSummaryDialogProps> = ({ open, patientId, summaryId, onClose, onChanged }) => {
  const id = useId();
  const { user } = useAuth();
  const { showToast } = useToast();
  const canUpdate = useCanPerform('update:dischargesummary');
  const canSign = useCanPerform('sign:dischargesummary');
  const canQa = useCanPerform('qa:dischargesummary');
  const canSend = useCanPerform('send:dischargesummary');

  const [summary, setSummary] = useState<DischargeSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [narrative, setNarrative] = useState<DischargeSummaryNarrative>({});
  const [goalOutcomes, setGoalOutcomes] = useState<Record<string, GoalOutcome>>({});
  const [awareDate, setAwareDate] = useState('');
  const [mode, setMode] = useState<Mode>('view');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [delivery, setDelivery] = useState<AddDeliveryRequest | null>(null);

  const adopt = useCallback((s: DischargeSummary) => {
    setSummary(s);
    setNarrative(s.narrative ?? {});
    setGoalOutcomes(s.goalOutcomes ?? {});
    setAwareDate(s.awareDate ?? '');
  }, []);

  useEffect(() => {
    if (!open || !summaryId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setError(null);
    setMode('view');
    setDelivery(null);
    DischargeSummaryService.get(patientId, summaryId)
      .then((s) => !cancelled && adopt(s))
      .catch((err) => !cancelled && setLoadError(describeApiError(err, 'Could not load the summary.')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, patientId, summaryId, adopt]);

  const run = async (work: () => Promise<DischargeSummary>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      const next = await work();
      adopt(next);
      onChanged?.(next);
      showToast(message, { variant: 'success' });
      return true;
    } catch (err) {
      setError(describeApiError(err, 'The change was not saved.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!summary) return;
    setBusy(true);
    setError(null);
    try {
      const link = await DischargeSummaryService.getPdfLink(patientId, summary.summaryId);
      window.open(link.url, '_blank', 'noopener');
    } catch (err) {
      setError(describeApiError(err, 'Could not prepare the PDF.'));
    } finally {
      setBusy(false);
    }
  };

  const titleId = `${id}-title`;
  if (!open) return null;

  const isAuthor = Boolean(summary && (!summary.authorId || (user?.id && String(summary.authorId) === String(user.id))));
  const isDraft = summary?.status === 'DRAFT';
  const isSigned = summary?.status === 'SIGNED';
  const editing = Boolean(summary && canUpdate && isAuthor && (isDraft || (isSigned && mode === 'amend')));
  const fields = summary ? NARRATIVE_FIELDS[summary.kind] : [];
  const facts = summary?.facts;
  const narrativeChanged = summary ? JSON.stringify(narrative) !== JSON.stringify(summary.narrative ?? {}) : false;
  const outcomesChanged = summary ? JSON.stringify(goalOutcomes) !== JSON.stringify(summary.goalOutcomes ?? {}) : false;
  const awareChanged = summary?.kind === 'TRANSFER' && (awareDate || null) !== (summary.awareDate ?? null);
  const dirty = narrativeChanged || outcomesChanged || awareChanged;

  const saveDraft = () => summary && run(() => DischargeSummaryService.update(patientId, summary.summaryId, {
    narrative, goalOutcomes, ...(summary.kind === 'TRANSFER' ? { awareDate: awareDate || null } : {}),
  }), 'Summary saved.');

  /** Save unsaved edits first, then sign or send to QA */
  const signOrSubmit = async (submitForQa: boolean) => {
    if (!summary) return;
    if (dirty && !(await saveDraft())) return;
    await run(() => DischargeSummaryService.sign(patientId, summary.summaryId, submitForQa), submitForQa ? 'Sent to QA.' : 'Summary signed.');
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} labelledBy={titleId} size="xl">
      <div className="text-left">
        <h3 id={titleId} className="pr-6 text-lg font-semibold text-slate-800">
          {summary ? KIND_LABEL[summary.kind] : 'Summary'}
        </h3>
        {loading && <p className="mt-2 text-sm text-slate-600">Loading…</p>}
        {loadError && <p role="alert" className="mt-2 text-sm text-red-700">{loadError}</p>}

        {summary && facts && (
          <>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="summary-status">
              <span className="rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 font-medium text-slate-800">
                {STATUS_LABEL[summary.status]} · v{summary.version}
              </span>
              <span className={`rounded-full border px-2 py-0.5 font-medium ${DUE_STYLE[summary.dueState]}`}>
                {DUE_LABEL[summary.dueState]}
                {summary.dueState !== 'SENT' && summary.dueState !== 'NOT_REQUIRED' ? ` ${formatUsDate(summary.dueDate)}` : ''}
              </span>
              <span className="text-slate-600">
                {summary.kind === 'TRANSFER' ? 'Transfer' : 'Discharge'} date {formatUsDate(summary.anchorDate)}
              </span>
            </div>
            {summary.qaReturnNote && isDraft && (
              <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                Returned by QA: {summary.qaReturnNote}
              </p>
            )}
            {!isAuthor && isDraft && (
              <p className="mt-2 text-xs text-slate-600">Only the author can edit and sign this summary.</p>
            )}

            <section className="mt-4" aria-labelledby={`${id}-facts`}>
              <div className="flex items-center justify-between gap-2">
                <h4 id={`${id}-facts`} className="text-sm font-semibold text-slate-800">From the record</h4>
                {canUpdate && (isDraft || summary.status === 'IN_QA') && (
                  <button
                    type="button"
                    className={secondary}
                    disabled={busy}
                    onClick={() => run(() => DischargeSummaryService.refresh(patientId, summary.summaryId), 'Updated from the record.')}
                  >
                    <RefreshCw className="mr-1 inline h-4 w-4" aria-hidden="true" /> Refresh from record
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">Fix these in the chart, then refresh. Updated {formatUsDate(summary.factsRefreshedAt)}.</p>
              <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="font-medium text-slate-700">Diagnoses</dt>
                  <dd className="text-slate-800">
                    {facts.diagnoses.length === 0 ? 'None recorded' : facts.diagnoses.map((d) => `${d.code ?? ''} ${d.description ?? ''}${d.isPrimary ? ' (primary)' : ''}`.trim()).join('; ')}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-slate-700">Allergies</dt>
                  <dd className="text-slate-800">
                    {facts.allergiesNoneKnown ? 'No known allergies' : facts.allergies.length === 0 ? 'None recorded' : facts.allergies.map((a) => a.allergen).join(', ')}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-slate-700">Medications on {formatUsDate(facts.asOfDate)}</dt>
                  <dd className="text-slate-800">
                    {facts.medications.length === 0 ? 'None recorded' : facts.medications.map((m) => [m.name, m.dose, m.route, m.frequency].filter(Boolean).join(' ')).join('; ')}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-slate-700">Equipment (DME)</dt>
                  <dd className="text-slate-800">{facts.dmeEquipment.length === 0 ? 'None recorded' : facts.dmeEquipment.join(', ').replace(/_/g, ' ').toLowerCase()}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="font-medium text-slate-700">Disciplines served</dt>
                  <dd className="text-slate-800">
                    {facts.disciplines.length === 0 ? 'No completed visits' : facts.disciplines.map((d) => `${d.discipline}: ${d.visits} visits, ${formatUsDate(d.firstVisit)}–${formatUsDate(d.lastVisit)}`).join('; ')}
                  </dd>
                </div>
              </dl>
            </section>

            {summary.kind === 'DISCHARGE' && facts.goals.length > 0 && (
              <section className="mt-4" aria-labelledby={`${id}-goals`}>
                <h4 id={`${id}-goals`} className="text-sm font-semibold text-slate-800">Care plan goals</h4>
                <ul className="mt-2 space-y-2">
                  {facts.goals.map((g) => (
                    <li key={g.goalId} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-sm text-slate-800">{g.discipline} · {g.category}: {g.goal}</span>
                      <select
                        aria-label={`Outcome for ${g.category} goal`}
                        value={goalOutcomes[g.goalId] ?? 'UNRESOLVED'}
                        disabled={!editing || busy}
                        onChange={(e) => setGoalOutcomes((prev) => ({ ...prev, [g.goalId]: e.target.value as GoalOutcome }))}
                        className="rounded-md border border-slate-300 bg-white p-1.5 text-sm"
                      >
                        {GOAL_OUTCOME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="mt-4 space-y-3" aria-label="Narrative">
              {summary.kind === 'TRANSFER' && (
                <div>
                  <label htmlFor={`${id}-aware`} className="block text-sm font-medium text-slate-700">Agency learned of the transfer on</label>
                  <input
                    id={`${id}-aware`}
                    type="date"
                    value={awareDate}
                    min={summary.anchorDate}
                    disabled={!editing || mode === 'amend' || busy}
                    onChange={(e) => setAwareDate(e.target.value)}
                    className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm sm:w-auto"
                  />
                  <p className="mt-1 text-xs text-slate-500">Leave empty when it was the same day. A later date moves the 2-business-day deadline.</p>
                </div>
              )}
              {fields.map(({ field, label, rows }) => (
                <div key={field}>
                  <label htmlFor={`${id}-${field}`} className="block text-sm font-medium text-slate-700">{label}</label>
                  <textarea
                    id={`${id}-${field}`}
                    rows={rows ?? 2}
                    maxLength={4000}
                    value={narrative[field] ?? ''}
                    readOnly={!editing}
                    disabled={busy}
                    onChange={(e) => setNarrative((prev) => ({ ...prev, [field]: e.target.value }))}
                    className={`mt-1 w-full rounded-md border p-2 text-sm ${editing ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50'}`}
                  />
                </div>
              ))}
            </section>

            {(summary.deliveries.length > 0 || summary.versions.length > 0 || summary.lastAmendment) && (
              <section className="mt-4 text-sm" aria-label="History">
                {summary.deliveries.length > 0 && (
                  <>
                    <h4 className="font-semibold text-slate-800">Sent to</h4>
                    <ul className="mt-1 space-y-1 text-slate-700" data-testid="summary-deliveries">
                      {summary.deliveries.map((d) => (
                        <li key={d.deliveryId}>
                          {formatUsDate(d.sentDate)} · {d.recipientName} ({labelOf(RECIPIENT_ROLE_OPTIONS, d.recipientRole)}) by {labelOf(DELIVERY_METHOD_OPTIONS, d.method)} · v{d.version}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {summary.lastAmendment && (
                  <p className="mt-2 text-xs text-slate-600">
                    Amended {formatUsDate(summary.lastAmendment.at)} (v{summary.lastAmendment.version}): {summary.lastAmendment.reason}.
                    {summary.versions.length > 0 && ` ${summary.versions.length} earlier version${summary.versions.length === 1 ? '' : 's'} kept.`}
                  </p>
                )}
              </section>
            )}

            {delivery && (
              <section className="mt-4 rounded-md border border-slate-200 p-3" aria-label="Mark sent">
                <h4 className="text-sm font-semibold text-slate-800">Mark sent</h4>
                <p className="text-xs text-slate-600">Record where you sent the PDF (fax, mail, portal…). Pelora does not send it.</p>
                <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="text-sm text-slate-700">Recipient
                    <input type="text" maxLength={120} value={delivery.recipientName}
                      onChange={(e) => setDelivery({ ...delivery, recipientName: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm" />
                  </label>
                  <label className="text-sm text-slate-700">Role
                    <select value={delivery.recipientRole} onChange={(e) => setDelivery({ ...delivery, recipientRole: e.target.value as AddDeliveryRequest['recipientRole'] })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white p-2 text-sm">
                      {RECIPIENT_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                  <label className="text-sm text-slate-700">Sent by
                    <select value={delivery.method} onChange={(e) => setDelivery({ ...delivery, method: e.target.value as AddDeliveryRequest['method'] })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white p-2 text-sm">
                      {DELIVERY_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                  <label className="text-sm text-slate-700">Sent on
                    <input type="date" value={delivery.sentDate} max={today()} min={summary.anchorDate}
                      onChange={(e) => setDelivery({ ...delivery, sentDate: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm" />
                  </label>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <button type="button" className={secondary} disabled={busy} onClick={() => setDelivery(null)}>Cancel</button>
                  <button
                    type="button"
                    className={primary}
                    disabled={busy || !delivery.recipientName.trim() || !delivery.sentDate}
                    onClick={async () => {
                      if (await run(() => DischargeSummaryService.addDelivery(patientId, summary.summaryId, { ...delivery, recipientName: delivery.recipientName.trim() }), 'Delivery recorded.')) {
                        setDelivery(null);
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </section>
            )}

            {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button type="button" className={secondary} disabled={busy} onClick={downloadPdf}>
                <Download className="mr-1 inline h-4 w-4" aria-hidden="true" /> {isSigned ? 'Download PDF' : 'Preview PDF'}
              </button>
              {summary.kind === 'TRANSFER' && (isDraft || summary.status === 'IN_QA') && canUpdate && (
                <button type="button" className={secondary} disabled={busy} onClick={() => setPending('close')}>Not required</button>
              )}
              {isDraft && editing && (
                <button type="button" className={secondary} disabled={busy || !dirty} onClick={saveDraft}>Save draft</button>
              )}
              {isDraft && editing && canSign && (
                <>
                  <button type="button" className={secondary} disabled={busy} onClick={() => signOrSubmit(true)}>Send to QA</button>
                  <button type="button" className={primary} disabled={busy} onClick={() => setPending('sign')}>Sign</button>
                </>
              )}
              {summary.status === 'IN_QA' && canQa && !isAuthor && (
                <>
                  <button type="button" className={secondary} disabled={busy} onClick={() => setPending('return')}>Return to author</button>
                  <button type="button" className={primary} disabled={busy} onClick={() => run(() => DischargeSummaryService.review(patientId, summary.summaryId, 'APPROVE'), 'Approved and signed.')}>Approve</button>
                </>
              )}
              {isSigned && canSend && !delivery && (
                <button type="button" className={secondary} disabled={busy}
                  onClick={() => setDelivery({ recipientName: facts.pcpName ?? '', recipientRole: 'PCP', method: 'FAX', sentDate: today() })}>
                  Mark sent
                </button>
              )}
              {isSigned && canSign && isAuthor && mode === 'view' && (
                <button type="button" className={secondary} disabled={busy} onClick={() => setMode('amend')}>Amend</button>
              )}
              {isSigned && mode === 'amend' && (
                <>
                  <button type="button" className={secondary} disabled={busy} onClick={() => { adopt(summary); setMode('view'); }}>Cancel amendment</button>
                  <button type="button" className={primary} disabled={busy || !dirty} onClick={() => setPending('amend')}>Save amendment</button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={pending === 'sign'}
        title="Sign summary"
        message="You are signing this summary electronically as its author. Signing locks it; later changes need an amendment with a reason."
        confirmLabel="Sign"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={async () => {
          setPending(null);
          await signOrSubmit(false);
        }}
      />
      <ReasonDialog
        open={pending === 'amend'}
        title="Amend signed summary"
        description="The signed version is kept. Say what changed and why."
        minLength={10}
        maxLength={500}
        confirmLabel="Save amendment"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={async (reason) => {
          if (!summary) return;
          setPending(null);
          if (await run(() => DischargeSummaryService.amend(patientId, summary.summaryId, { reason, narrative, goalOutcomes }), 'Summary amended.')) setMode('view');
        }}
      />
      <ReasonDialog
        open={pending === 'return'}
        title="Return to author"
        label="What needs to change"
        minLength={5}
        maxLength={500}
        confirmLabel="Return"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={async (note) => {
          if (!summary) return;
          setPending(null);
          await run(() => DischargeSummaryService.review(patientId, summary.summaryId, 'RETURN', note), 'Returned to the author.');
        }}
      />
      <ReasonDialog
        open={pending === 'close'}
        title="Transfer summary not required"
        description="For example, the patient came home before a summary was needed."
        minLength={10}
        maxLength={500}
        confirmLabel="Close summary"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={async (reason) => {
          if (!summary) return;
          setPending(null);
          await run(() => DischargeSummaryService.close(patientId, summary.summaryId, reason), 'Transfer summary closed.');
        }}
      />
    </Dialog>
  );
};

export default DischargeSummaryDialog;
